import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import LinearProgress from '@material-ui/core/LinearProgress';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import RefreshIcon from '@material-ui/icons/Refresh';
import { makeStyles } from '@material-ui/core/styles';

import { actions as whatsappActions } from '../actions';
import ConfirmDialog from '../components/ConfirmDialog';
import ProfilePictureEditor from '../components/ProfilePictureEditor';
import { colors } from '../theme';
import { toKyivWall } from '../kyivTime';

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ ...whatsappActions }, dispatch);

const REFRESH_MS = 60000;

const LIGHT = {
  ok: colors.green,
  warn: '#ffb300',
  error: colors.red,
  idle: '#b0bec5',
};

// Что значит каждое состояние для менеджера: цвет светофора, короткий ответ и совет.
const QUALITY = {
  GREEN: { kind: 'ok', text: 'Хорошее', hint: 'Клиенты на вас не жалуются. Так и держать.' },
  YELLOW: {
    kind: 'warn',
    text: 'Среднее',
    hint: 'Жалоб или блокировок стало больше. Пишите только тем, кто согласился, и не рассылайте всем подряд.',
  },
  RED: {
    kind: 'error',
    text: 'Низкое',
    hint: 'Meta может снизить лимит или остановить отправку. Приостановите рассылки.',
  },
  UNKNOWN: { kind: 'idle', text: 'Пока неизвестно', hint: 'Мало сообщений, чтобы оценить.' },
};

const CAN_SEND = {
  AVAILABLE: { kind: 'ok', text: 'Доступна', hint: 'Meta разрешает отправку.' },
  LIMITED: { kind: 'warn', text: 'Ограничена', hint: 'Есть ограничения у номера или аккаунта, смотрите в кабинете Meta.' },
  BLOCKED: { kind: 'error', text: 'Заблокирована', hint: 'Meta не даёт отправлять. Причину смотрите в кабинете Meta.' },
};

const REVIEW = {
  APPROVED: { kind: 'ok', text: 'Одобрен', hint: 'Аккаунт прошёл проверку Meta.' },
  PENDING: { kind: 'warn', text: 'На проверке', hint: 'Meta ещё проверяет аккаунт.' },
  REJECTED: { kind: 'error', text: 'Отклонён', hint: 'Аккаунт не прошёл проверку Meta.' },
};

const VERIFICATION = {
  verified: { kind: 'ok', text: 'Подтверждён', hint: 'Компания подтверждена в Meta: лимиты выше.' },
  not_verified: { kind: 'warn', text: 'Не подтверждён', hint: 'Без подтверждения компании лимиты рассылок остаются низкими.' },
  pending_submission: { kind: 'warn', text: 'Не завершено', hint: 'Подтверждение компании начато, но не отправлено.' },
  pending: { kind: 'warn', text: 'На проверке', hint: 'Meta проверяет документы компании.' },
};

const NAME_STATUS = {
  APPROVED: 'Одобрено',
  AVAILABLE_WITHOUT_REVIEW: 'Доступно без проверки',
  PENDING_REVIEW: 'На проверке',
  DECLINED: 'Отклонено',
  EXPIRED: 'Истекло',
  NON_EXISTS: 'Не задано',
  NONE: 'Не задано',
};

const VERTICALS = [
  ['UNDEFINED', 'Не указана'],
  ['OTHER', 'Другое'],
  ['AUTO', 'Авто'],
  ['BEAUTY', 'Красота'],
  ['APPAREL', 'Одежда'],
  ['EDU', 'Образование'],
  ['ENTERTAIN', 'Развлечения'],
  ['EVENT_PLAN', 'Организация событий'],
  ['FINANCE', 'Финансы'],
  ['GROCERY', 'Продукты'],
  ['GOVT', 'Госслужба'],
  ['HOTEL', 'Отели'],
  ['HEALTH', 'Здоровье'],
  ['NONPROFIT', 'Некоммерческая организация'],
  ['PROF_SERVICES', 'Профессиональные услуги'],
  ['RETAIL', 'Розница'],
  ['TRAVEL', 'Путешествия'],
  ['RESTAURANT', 'Рестораны'],
  ['NOT_A_BIZ', 'Не бизнес'],
];

const PART_NAMES = {
  phone: 'номеру',
  waba: 'аккаунту',
  profile: 'профилю компании',
  subscribed: 'подписке на события',
};

const useStyles = makeStyles((theme) => ({
  toolbar: { display: 'flex', alignItems: 'center', marginBottom: theme.spacing(3) },
  spacer: { flexGrow: 1 },
  updated: { color: colors.textDim, fontSize: '12px', marginRight: theme.spacing(1) },
  banner: {
    padding: '12px 16px',
    borderRadius: 8,
    marginBottom: theme.spacing(3),
    fontSize: '13px',
  },
  bannerWarn: { backgroundColor: colors.warnBg, color: colors.warnText },
  bannerError: { backgroundColor: '#ffebee', color: '#b71c1c' },
  light: {
    padding: '16px 18px',
    borderRadius: 10,
    height: '100%',
    borderTop: '4px solid transparent',
  },
  lightLabel: { color: colors.textDim, fontSize: '12px', fontWeight: 600, marginBottom: 6 },
  lightValue: { display: 'flex', alignItems: 'center', fontSize: '20px', fontWeight: 700, color: colors.text },
  dot: { width: 11, height: 11, borderRadius: '50%', marginRight: 9, flexShrink: 0 },
  lightHint: { color: colors.textDim, fontSize: '12px', marginTop: 6, lineHeight: 1.4 },
  panel: { padding: '20px 22px', borderRadius: 10, height: '100%' },
  panelTitle: { color: colors.text, fontWeight: 700, fontSize: '15px', marginBottom: 4 },
  panelHint: { color: colors.textDim, fontSize: '12px', marginBottom: 14, lineHeight: 1.45 },
  big: { fontSize: '38px', fontWeight: 700, color: colors.text, lineHeight: 1.1 },
  bigNote: { color: colors.textDim, fontSize: '13px', marginLeft: 8 },
  track: { height: 12, borderRadius: 6, backgroundColor: colors.panelAlt, overflow: 'hidden', margin: '14px 0 8px' },
  fill: { height: '100%', transition: 'width 0.4s ease' },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    padding: '9px 0',
    borderBottom: `1px solid ${colors.border}`,
    fontSize: '13px',
  },
  rowLabel: { color: colors.textDim, marginRight: 16, flexShrink: 0 },
  rowValue: { color: colors.text, fontWeight: 600, textAlign: 'right', wordBreak: 'break-word' },
  note: { color: colors.textDim, fontSize: '12px', fontWeight: 400, marginTop: 2 },
  mono: { fontFamily: 'Consolas, Menlo, monospace', fontSize: '12px', wordBreak: 'break-all' },
  field: { marginBottom: 16 },
  formActions: { display: 'flex', alignItems: 'center', marginTop: 4 },
  error: { color: '#d32f2f', fontSize: '13px', marginLeft: 16 },
  saved: { color: colors.okText, fontSize: '13px', marginLeft: 16 },
}));

const clock = (iso) => {
  const d = toKyivWall(iso);
  return `${`0${d.getHours()}`.slice(-2)}:${`0${d.getMinutes()}`.slice(-2)}`;
};
const number = (n) => Number(n || 0).toLocaleString('ru-RU');

const emptyForm = {
  about: '',
  description: '',
  address: '',
  email: '',
  site1: '',
  site2: '',
  vertical: 'UNDEFINED',
};

const toForm = (profile) => {
  const sites = (profile && profile.websites) || [];
  return {
    about: (profile && profile.about) || '',
    description: (profile && profile.description) || '',
    address: (profile && profile.address) || '',
    email: (profile && profile.email) || '',
    site1: sites[0] || '',
    site2: sites[1] || '',
    vertical: (profile && profile.vertical) || 'UNDEFINED',
  };
};

const sitesOf = (form) => [form.site1.trim(), form.site2.trim()].filter(Boolean);

// Только то, что менеджер изменил: остальное Meta не трогаем.
const changesOf = (base, form) => {
  const changes = {};
  ['about', 'description', 'address', 'email', 'vertical'].forEach((key) => {
    if (form[key].trim() !== base[key].trim()) changes[key] = form[key].trim();
  });
  if (JSON.stringify(sitesOf(form)) !== JSON.stringify(sitesOf(base))) {
    changes.websites = sitesOf(form);
  }
  return changes;
};

function Light({ classes, label, info }) {
  const state = info || { kind: 'idle', text: '—', hint: 'Нет данных от Meta.' };
  return (
    <Paper className={classes.light} style={{ borderTopColor: LIGHT[state.kind] }}>
      <div className={classes.lightLabel}>{label}</div>
      <div className={classes.lightValue}>
        <span className={classes.dot} style={{ backgroundColor: LIGHT[state.kind] }} />
        {state.text}
      </div>
      <div className={classes.lightHint}>{state.hint}</div>
    </Paper>
  );
}

function Account(props) {
  const classes = useStyles();

  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState(emptyForm);
  const [base, setBase] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saved, setSaved] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Адрес вебхука: поле, ожидание ответа Meta и результат.
  const [hookEditing, setHookEditing] = useState(false);
  const [hookUrl, setHookUrl] = useState('');
  const [hookConfirm, setHookConfirm] = useState(false);
  const [hookBusy, setHookBusy] = useState(false);
  const [hookError, setHookError] = useState('');
  const [hookDone, setHookDone] = useState('');

  const dirty = useMemo(() => Object.keys(changesOf(base, form)).length > 0, [base, form]);

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    const result = await props.fetchAccount(refresh);
    setLoading(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setError('');
    setData(result.data);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
    const timer = setInterval(() => load(), REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  // Профиль в форму попадает с сервера, но не перетирает то, что менеджер уже правит.
  useEffect(() => {
    if (data && data.profile && !dirty) {
      const next = toForm(data.profile);
      setBase(next);
      setForm(next);
    }
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const setField = (key) => (event) => {
    setForm({ ...form, [key]: event.target.value });
    setSaved(false);
    setSaveError('');
  };

  const handleSave = async () => {
    setConfirmOpen(false);
    setSaving(true);
    setSaveError('');
    const result = await props.saveProfile(changesOf(base, form));
    setSaving(false);
    if (result.ok) {
      const next = toForm(result.data.profile);
      setBase(next);
      setForm(next);
      setSaved(true);
      load(true);
    } else {
      setSaveError(result.error);
    }
  };

  const handleHookSave = async () => {
    setHookConfirm(false);
    setHookBusy(true);
    setHookError('');
    setHookDone('');
    const result = await props.saveWebhook(hookUrl.trim());
    setHookBusy(false);
    if (!result.ok) {
      setHookError(result.error);
      return;
    }
    setHookEditing(false);
    setHookUrl('');
    setHookDone('Meta приняла адрес и проверила его.');
    load(true);
  };

  const handleHookReset = async () => {
    setHookBusy(true);
    setHookError('');
    setHookDone('');
    const result = await props.resetWebhook();
    setHookBusy(false);
    if (!result.ok) {
      setHookError(result.error);
      return;
    }
    setHookDone('Вернули адрес из настроек приложения Meta.');
    load(true);
  };

  const phone = data && data.phone;
  const waba = data && data.waba;
  const limits = data && data.limits;
  const webhook = data && data.webhook;

  const usedShare =
    limits && limits.limit ? Math.min(1, limits.used / limits.limit) : 0;
  const limitColor = usedShare > 0.9 ? colors.red : usedShare > 0.7 ? '#ffb300' : colors.accent;

  const nameHint =
    phone && (phone.nameStatus === 'NON_EXISTS' || phone.nameStatus === 'NONE' || phone.nameStatus === 'DECLINED')
      ? 'Одобренного названия нет: клиенты могут видеть просто номер. Проверьте в кабинете Meta: WhatsApp Manager → Номера телефонов → Отображаемое имя.'
      : '';

  return (
    <div>
      <div className={classes.toolbar}>
        <div className={classes.spacer} />
        {data && <span className={classes.updated}>Обновлено в {clock(data.generatedAt)}</span>}
        <Tooltip title="Обновить у Meta">
          <IconButton size="small" onClick={() => load(true)} aria-label="Обновить">
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>

      {loading && !data && <LinearProgress style={{ marginBottom: 24 }} />}
      {error && <div className={`${classes.banner} ${classes.bannerError}`}>{error}</div>}

      {data && data.testMode.on && (
        <div className={`${classes.banner} ${classes.bannerWarn}`}>
          Включён тестовый режим: сервер отправляет сообщения только на {data.testMode.numbers} разрешённых
          номера. Для боевой рассылки список надо очистить (переменная WHATSAPP_TEST_RECIPIENTS на сервере).
        </div>
      )}

      {data && data.errors.length > 0 && (
        <div className={`${classes.banner} ${classes.bannerWarn}`}>
          Meta не ответила по: {data.errors.map((part) => PART_NAMES[part] || part).join(', ')}. Остальное показано.
        </div>
      )}

      {data && (
        <>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} lg={3}>
              <Light classes={classes} label="Качество номера" info={phone && (QUALITY[phone.quality] || QUALITY.UNKNOWN)} />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <Light classes={classes} label="Отправка сообщений" info={waba && CAN_SEND[waba.canSend]} />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <Light classes={classes} label="Аккаунт в Meta" info={waba && REVIEW[waba.review]} />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <Light classes={classes} label="Компания" info={waba && VERIFICATION[waba.verification]} />
            </Grid>
          </Grid>

          <Grid container spacing={3} style={{ marginTop: 4 }}>
            <Grid item xs={12} lg={6}>
              <Paper className={classes.panel}>
                <div className={classes.panelTitle}>Лимит рассылок на сегодня</div>
                <div className={classes.panelHint}>
                  Номер может начать разговор не более чем с определённым числом разных людей за 24 часа.
                  Тем, с кем вы уже писали сегодня, можно писать ещё без расхода лимита.
                </div>
                {limits && limits.limit !== null ? (
                  <>
                    <div>
                      <span className={classes.big}>{number(limits.remaining)}</span>
                      <span className={classes.bigNote}>
                        осталось из {number(limits.limit)} за сутки
                      </span>
                    </div>
                    <div className={classes.track}>
                      <div className={classes.fill} style={{ width: `${usedShare * 100}%`, backgroundColor: limitColor }} />
                    </div>
                    <div className={classes.panelHint} style={{ marginBottom: 0 }}>
                      Уже писали за последние 24 часа: {number(limits.used)}. Если в рассылке новых людей больше,
                      чем осталось, сервер её не отправит.
                    </div>
                    <div className={classes.panelHint} style={{ marginTop: 10, marginBottom: 0 }}>
                      Лимит растёт сам (до 1 000, потом 10 000), если качество номера зелёное и за последние
                      дни вы использовали больше половины лимита.
                    </div>
                  </>
                ) : (
                  <div className={classes.panelHint} style={{ marginBottom: 0 }}>
                    {limits && limits.tier === 'TIER_UNLIMITED'
                      ? 'У номера нет дневного ограничения.'
                      : 'Meta не сообщила лимит номера, поэтому сервер его не проверяет.'}
                  </div>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} lg={6}>
              <Paper className={classes.panel}>
                <div className={classes.panelTitle}>Номер и имя</div>
                <div className={classes.panelHint}>Так вас видят клиенты и так номер подключён</div>
                {phone ? (
                  <>
                    <div className={classes.row}>
                      <span className={classes.rowLabel}>Номер</span>
                      <span className={classes.rowValue}>{phone.number}</span>
                    </div>
                    <div className={classes.row}>
                      <span className={classes.rowLabel}>Имя для клиентов</span>
                      <span className={classes.rowValue}>{phone.verifiedName || '—'}</span>
                    </div>
                    <div className={classes.row}>
                      <span className={classes.rowLabel}>Статус имени</span>
                      <span className={classes.rowValue}>
                        {NAME_STATUS[phone.nameStatus] || phone.nameStatus || '—'}
                        {nameHint && <div className={classes.note}>{nameHint}</div>}
                      </span>
                    </div>
                    <div className={classes.row}>
                      <span className={classes.rowLabel}>Режим</span>
                      <span className={classes.rowValue}>
                        {phone.mode === 'LIVE' ? 'Боевой' : phone.mode || '—'} · {phone.status === 'CONNECTED' ? 'подключён' : phone.status || '—'}
                      </span>
                    </div>
                    <div className={classes.row}>
                      <span className={classes.rowLabel}>Двухэтапная проверка (PIN)</span>
                      <span className={classes.rowValue}>
                        {phone.pinEnabled ? 'Включена' : 'Не включена'}
                        {!phone.pinEnabled && (
                          <div className={classes.note}>
                            Защищает номер от угона. Включается в кабинете Meta: WhatsApp Manager → Номера
                            телефонов → Двухэтапная проверка.
                          </div>
                        )}
                      </span>
                    </div>
                    {waba && (
                      <div className={classes.row} style={{ borderBottom: 'none' }}>
                        <span className={classes.rowLabel}>Валюта счёта Meta</span>
                        <span className={classes.rowValue}>{waba.currency || '—'}</span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className={classes.panelHint}>Meta не сообщила данные номера.</div>
                )}
              </Paper>
            </Grid>
          </Grid>

          <Grid container spacing={3} style={{ marginTop: 4 }}>
            <Grid item xs={12}>
              <Paper className={classes.panel}>
                <div className={classes.panelTitle}>Вебхук: ответы клиентов и статусы доставки</div>
                <div className={classes.panelHint}>
                  Через этот адрес Meta присылает ответы клиентов и отметки «доставлено / прочитано».
                </div>
                <div className={classes.row}>
                  <span className={classes.rowLabel}>Адрес</span>
                  <span className={`${classes.rowValue} ${classes.mono}`}>
                    {webhook.url || 'не задан'}
                    {/ngrok|localhost|127\.0\.0\.1/i.test(webhook.url) && (
                      <div className={classes.note} style={{ fontFamily: 'inherit' }}>
                        Временный адрес: когда туннель остановится, Meta перестанет доставлять события.
                      </div>
                    )}
                  </span>
                </div>
                <div className={classes.row}>
                  <span className={classes.rowLabel}>Откуда адрес</span>
                  <span className={classes.rowValue}>
                    {webhook.overridden ? 'Задан отсюда' : 'Из настроек приложения Meta'}
                  </span>
                </div>
                <div className={classes.row}>
                  <span className={classes.rowLabel}>Сменить адрес</span>
                  <span className={classes.rowValue} style={{ fontWeight: 400, flexGrow: 1, maxWidth: 640 }}>
                    {hookEditing ? (
                      <>
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          autoFocus
                          placeholder="https://имя.ngrok-free.app/api/whatsapp/webhook"
                          value={hookUrl}
                          disabled={hookBusy}
                          onChange={(event) => {
                            setHookUrl(event.target.value);
                            setHookError('');
                          }}
                          inputProps={{ 'aria-label': 'Новый адрес вебхука' }}
                        />
                        <div className={classes.note}>
                          Адрес должен быть публичным (https) и заканчиваться на /api/whatsapp/webhook.
                        </div>
                        <div className={classes.note}>
                          Адрес и токен проверки уходят в Meta одним запросом. Токен сервер берёт сам
                          (WHATSAPP_VERIFY_TOKEN): вводить его не нужно.
                        </div>
                        <div className={classes.formActions}>
                          <Button
                            variant="contained"
                            color="primary"
                            disableElevation
                            disabled={hookBusy || !hookUrl.trim()}
                            onClick={() => setHookConfirm(true)}
                          >
                            Применить
                          </Button>
                          <Button
                            disabled={hookBusy}
                            onClick={() => {
                              setHookEditing(false);
                              setHookUrl('');
                              setHookError('');
                            }}
                            style={{ marginLeft: 8 }}
                          >
                            Отмена
                          </Button>
                          {hookBusy && (
                            <span className={classes.saved} style={{ color: colors.textDim }}>
                              Meta проверяет адрес…
                            </span>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={hookBusy}
                          onClick={() => {
                            setHookEditing(true);
                            setHookDone('');
                          }}
                        >
                          Изменить адрес
                        </Button>
                        {webhook.overridden && (
                          <Button size="small" disabled={hookBusy} onClick={handleHookReset} style={{ marginLeft: 8 }}>
                            Вернуть адрес приложения
                          </Button>
                        )}
                      </>
                    )}
                    {hookError && (
                      <div className={classes.error} style={{ marginLeft: 0, marginTop: 8 }}>
                        {hookError}
                      </div>
                    )}
                    {hookDone && (
                      <div className={classes.saved} style={{ marginLeft: 0, marginTop: 8 }}>
                        {hookDone}
                      </div>
                    )}
                  </span>
                </div>
                <div className={classes.row}>
                  <span className={classes.rowLabel}>Подписка приложения на события</span>
                  <span className={classes.rowValue}>
                    {webhook.subscribed === null ? 'неизвестно' : webhook.subscribed ? 'Есть' : 'Нет: сообщения не будут приходить'}
                  </span>
                </div>
                <div className={classes.row} style={{ borderBottom: 'none' }}>
                  <span className={classes.rowLabel}>Последнее событие</span>
                  <span className={classes.rowValue}>
                    {webhook.lastEventAt
                      ? `сегодня в ${clock(webhook.lastEventAt)}`
                      : 'с запуска сервера ничего не приходило'}
                  </span>
                </div>
              </Paper>
            </Grid>
          </Grid>

          <Grid container spacing={3} style={{ marginTop: 4 }}>
            <Grid item xs={12}>
              <Paper className={classes.panel}>
                <div className={classes.panelTitle}>Профиль компании</div>
                <div className={classes.panelHint}>
                  Эти данные видят клиенты, когда открывают ваш чат в WhatsApp. Чем полнее профиль, тем больше доверия.
                </div>
                <ProfilePictureEditor
                  current={(data.profile && data.profile.profile_picture_url) || ''}
                  onSaved={() => load(true)}
                />
                {data.profile ? (
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        className={classes.field}
                        label="О себе (коротко)"
                        value={form.about}
                        inputProps={{ maxLength: 139 }}
                        helperText={`${form.about.length} / 139`}
                        onChange={setField('about')}
                      />
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        variant="outlined"
                        size="small"
                        className={classes.field}
                        label="Описание"
                        value={form.description}
                        inputProps={{ maxLength: 512 }}
                        helperText={`${form.description.length} / 512`}
                        onChange={setField('description')}
                      />
                      <TextField
                        select
                        fullWidth
                        variant="outlined"
                        size="small"
                        className={classes.field}
                        label="Сфера деятельности"
                        value={form.vertical}
                        onChange={setField('vertical')}
                      >
                        {VERTICALS.map(([value, label]) => (
                          <MenuItem key={value} value={value}>
                            {label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        className={classes.field}
                        label="Адрес"
                        value={form.address}
                        inputProps={{ maxLength: 256 }}
                        onChange={setField('address')}
                      />
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        className={classes.field}
                        label="Почта"
                        value={form.email}
                        inputProps={{ maxLength: 128 }}
                        onChange={setField('email')}
                      />
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        className={classes.field}
                        label="Сайт 1"
                        placeholder="https://"
                        value={form.site1}
                        inputProps={{ maxLength: 256 }}
                        onChange={setField('site1')}
                      />
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        className={classes.field}
                        label="Сайт 2"
                        placeholder="https://"
                        value={form.site2}
                        inputProps={{ maxLength: 256 }}
                        onChange={setField('site2')}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <div className={classes.formActions}>
                        <Button
                          variant="contained"
                          color="primary"
                          disableElevation
                          disabled={!dirty || saving}
                          onClick={() => setConfirmOpen(true)}
                        >
                          Сохранить в WhatsApp
                        </Button>
                        {dirty && !saving && (
                          <Button style={{ marginLeft: 8 }} onClick={() => setForm(base)}>
                            Отменить изменения
                          </Button>
                        )}
                        {saved && !dirty && <span className={classes.saved}>Сохранено</span>}
                        {saveError && <span className={classes.error}>{saveError}</span>}
                      </div>
                    </Grid>
                  </Grid>
                ) : (
                  <div className={classes.panelHint}>Meta не отдала профиль компании.</div>
                )}
              </Paper>
            </Grid>
          </Grid>
        </>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="Изменить профиль в WhatsApp?"
        text="Эти данные сразу увидят клиенты в WhatsApp. Сохранить изменения?"
        confirmLabel="Сохранить"
        onConfirm={handleSave}
        onClose={() => setConfirmOpen(false)}
      />
      <ConfirmDialog
        open={hookConfirm}
        title="Сменить адрес вебхука?"
        text={`Meta начнёт присылать ответы клиентов и отметки доставки на ${hookUrl.trim()}. Если по этому адресу сервис не отвечает, ответы клиентов перестанут приходить, пока вы не вернёте рабочий адрес. Meta сначала проверит адрес и откажет, если он не отвечает.`}
        confirmLabel="Сменить адрес"
        onConfirm={handleHookSave}
        onClose={() => setHookConfirm(false)}
      />
    </div>
  );
}

export default connect(null, mapDispatchToProps)(Account);
